import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, X, Copy, Check, ExternalLink, Lock, Eye, EyeOff, Loader2, ChevronDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_PW_KEY = "ire_admin_pw";   // localStorage — persists across sessions on this device

// Read admin pw from either storage (legacy session entries still work)
const readAdminPw = () => {
  if (typeof window === "undefined") return "";
  return localStorage.getItem(ADMIN_PW_KEY) || sessionStorage.getItem(ADMIN_PW_KEY) || "";
};
const writeAdminPw = (val) => {
  if (typeof window === "undefined") return;
  localStorage.setItem(ADMIN_PW_KEY, val);
};

// Drop this widget anywhere on a project page.
// It manages its own admin auth + generator state.
// FAB is only rendered to users who have authenticated as admin on this device.
// To trigger first-time admin auth on a new device, append ?team=1 to the URL.

export const DealroomShareWidget = ({ project, projectLabel }) => {
  const [hasAdminPw, setHasAdminPw] = useState(() => {
    if (typeof window === "undefined") return false;
    // FAB is visible whenever the team password is stored locally — which happens
    // automatically when the team types it on the dealroom lock screen.
    // Clients reach the dealroom via magic link and never get this flag set.
    return !!readAdminPw();
  });
  const [open, setOpen] = useState(false);

  // Listen for localStorage changes (e.g. team just unlocked dealroom in same tab)
  useEffect(() => {
    if (hasAdminPw) return;
    const t = setInterval(() => {
      if (readAdminPw()) {
        setHasAdminPw(true);
        clearInterval(t);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [hasAdminPw]);

  // Reveal FAB only on team devices
  if (!hasAdminPw) return null;

  return (
    <>
      <motion.button
        type="button"
        onClick={() => setOpen(true)}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.5 }}
        data-testid="share-fab"
        aria-label="Share with client"
        className="fixed bottom-6 right-4 sm:bottom-8 sm:right-8 z-[80] flex items-center gap-2.5 pl-4 pr-5 py-3.5 bg-ire-accent text-black font-body text-xs tracking-[0.18em] uppercase shadow-[0_8px_30px_-6px_rgba(212,175,106,0.6)] hover:bg-white transition-all"
      >
        <Share2 className="w-4 h-4" />
        Share with client
      </motion.button>

      <AnimatePresence>
        {open && (
          <ShareSheet
            project={project}
            projectLabel={projectLabel}
            onClose={() => setOpen(false)}
            needsAuth={false}
            onAuthDone={() => setHasAdminPw(true)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

const ShareSheet = ({ project, projectLabel, onClose, needsAuth, onAuthDone }) => {
  const [authed, setAuthed] = useState(!needsAuth);
  const [pw, setPw] = useState(readAdminPw());
  const [showPw, setShowPw] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);

  const [client, setClient] = useState("");
  const [days, setDays] = useState(14);
  const [busy, setBusy] = useState(false);
  const [genErr, setGenErr] = useState("");
  const [lastUrl, setLastUrl] = useState("");
  const [lastExp, setLastExp] = useState("");
  const [copied, setCopied] = useState(false);

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Esc to close
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const verifyAndContinue = async (e) => {
    e?.preventDefault();
    setAuthBusy(true);
    setAuthError(false);
    try {
      const r = await fetch(`${BACKEND_URL}/api/dealroom/share/list`, { headers: { "X-Admin-Password": pw } });
      setAuthBusy(false);
      if (r.status === 401) {
        setAuthError(true);
        setTimeout(() => setAuthError(false), 1800);
        return;
      }
      sessionStorage.setItem(ADMIN_PW_KEY, pw);
      writeAdminPw(pw);
      onAuthDone();
      setAuthed(true);
    } catch {
      setAuthBusy(false);
      setAuthError(true);
    }
  };

  const generate = async (e) => {
    e?.preventDefault();
    setGenErr("");
    if (!client.trim()) { setGenErr("Please enter the client name."); return; }
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/dealroom/share/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Password": pw },
        body: JSON.stringify({ project, client: client.trim(), days: Number(days) }),
      });
      const data = await res.json();
      setBusy(false);
      if (!res.ok) {
        setGenErr(data.detail || "Failed to generate");
        return;
      }
      const projectPath = project === "amselweg-14b" ? "/projects/amselweg-14b" : "/projects";
      const url = `${window.location.origin}${projectPath}${data.url_suffix}`;
      setLastUrl(url);
      setLastExp(data.expires_at);
    } catch (e2) {
      setBusy(false);
      setGenErr(String(e2));
    }
  };

  const message = `Hi ${client || "[client]"}, here's the private investment case for ${projectLabel}. Link is valid until ${lastExp ? new Date(lastExp).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : ""}.\n\n${lastUrl}`;

  const shareNative = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${projectLabel} · Investment case for ${client}`,
          text: message,
          url: lastUrl,
        });
      } else {
        copy();
      }
    } catch { /* user cancelled */ }
  };

  const copy = () => {
    navigator.clipboard.writeText(lastUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const reset = () => {
    setClient("");
    setLastUrl("");
    setLastExp("");
    setCopied(false);
    setGenErr("");
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[120] bg-black/75 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="share-sheet-overlay"
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-md bg-[#0a0a0a] border-t sm:border border-white/[0.08] sm:mx-4 max-h-[92vh] overflow-y-auto"
        data-testid="share-sheet"
      >
        {/* Mobile grab handle */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-white/15 rounded-full" />
        </div>

        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="share-sheet-close"
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-white/55 hover:text-ire-accent hover:bg-white/[0.04] transition-all"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-6 sm:px-8 pt-6 pb-2">
          <div className="flex items-center gap-2 mb-2">
            <Share2 className="w-3.5 h-3.5 text-ire-accent" />
            <span className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase">IRE team · share link</span>
          </div>
          <h3 className="font-heading text-2xl text-white leading-tight">{projectLabel}</h3>
        </div>

        {/* Auth gate */}
        {!authed && (
          <form onSubmit={verifyAndContinue} className="px-6 sm:px-8 py-6">
            <div className="flex items-center gap-2 mb-4 text-amber-200/85">
              <Lock className="w-3.5 h-3.5" />
              <span className="font-body text-xs">First-time access on this device</span>
            </div>
            <label className="block">
              <span className="font-body text-[10px] tracking-[0.25em] uppercase text-white/55 mb-2 block">Team password</span>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  autoFocus
                  placeholder="Enter team password"
                  data-testid="share-auth-input"
                  className={`w-full bg-black/60 border ${authError ? "border-rose-400/60" : "border-white/[0.12] focus:border-ire-accent"} text-white font-mono text-base py-4 pl-4 pr-12 outline-none placeholder:text-white/25`}
                />
                <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-ire-accent" tabIndex={-1}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </label>
            {authError && <div className="mt-2 font-body text-xs text-rose-300">Wrong password — try again.</div>}
            <button type="submit" disabled={authBusy} data-testid="share-auth-submit" className="w-full mt-6 py-4 bg-ire-accent text-black font-body text-xs tracking-[0.25em] uppercase hover:bg-white transition-all disabled:opacity-40 flex items-center justify-center gap-2">
              {authBusy && <Loader2 className="w-4 h-4 animate-spin" />}
              {authBusy ? "Verifying…" : "Unlock team mode"}
            </button>
          </form>
        )}

        {/* Generator (shows after auth) */}
        {authed && !lastUrl && (
          <form onSubmit={generate} className="px-6 sm:px-8 py-6" data-testid="share-generator-form">
            <label className="block mb-5">
              <span className="font-body text-[10px] tracking-[0.25em] uppercase text-white/55 mb-2 block">Client name</span>
              <input
                type="text"
                value={client}
                onChange={(e) => setClient(e.target.value)}
                autoFocus
                placeholder="Hr. Schmidt · Wealth"
                data-testid="share-client-input"
                className="w-full bg-black/60 border border-white/[0.12] focus:border-ire-accent text-white font-body text-base py-4 px-4 outline-none placeholder:text-white/25"
              />
              <span className="block mt-1.5 font-body text-[11px] text-white/40">They&apos;ll see this as a personalised welcome.</span>
            </label>

            <label className="block mb-6">
              <span className="font-body text-[10px] tracking-[0.25em] uppercase text-white/55 mb-2 block">Link valid for · {days} days</span>
              <input
                type="range"
                min={1}
                max={90}
                step={1}
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value))}
                data-testid="share-days-slider"
                style={{ background: `linear-gradient(90deg,#D4AF6A 0%,#D4AF6A ${(days / 90) * 100}%,rgba(255,255,255,0.08) ${(days / 90) * 100}%,rgba(255,255,255,0.08) 100%)` }}
                className="ire-slider w-full h-1.5 rounded-full appearance-none cursor-pointer outline-none"
              />
              <div className="flex justify-between mt-1.5 font-mono text-[10px] text-white/35 tabular-nums">
                <span>1d</span><span>14d</span><span>30d</span><span>90d</span>
              </div>
            </label>

            {genErr && <div className="mb-4 font-body text-xs text-rose-300" data-testid="share-gen-error">{genErr}</div>}

            <button
              type="submit"
              disabled={busy}
              data-testid="share-generate-btn"
              className="w-full py-4 bg-ire-accent text-black font-body text-xs tracking-[0.25em] uppercase hover:bg-white transition-all disabled:opacity-40 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? "Generating…" : "Generate share link"}
            </button>
          </form>
        )}

        {/* Generated URL view */}
        {authed && lastUrl && (
          <div className="px-6 sm:px-8 py-6" data-testid="share-result">
            <div className="flex items-center gap-2 mb-3">
              <Check className="w-3.5 h-3.5 text-emerald-400" />
              <span className="font-body text-[10px] tracking-[0.3em] text-emerald-400 uppercase">Link ready · {client}</span>
            </div>
            <p className="font-body text-[11px] text-white/45 mb-4 leading-relaxed">
              Valid until {new Date(lastExp).toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}.
              {client && ` ${client} will see a personalised welcome.`}
            </p>

            {/* Primary share button — native sheet on mobile */}
            <button
              type="button"
              onClick={shareNative}
              data-testid="share-native-btn"
              className="w-full py-4 bg-ire-accent text-black font-body text-xs tracking-[0.25em] uppercase hover:bg-white transition-all flex items-center justify-center gap-2 mb-3"
            >
              <Share2 className="w-4 h-4" />
              Share link
            </button>

            {/* Secondary actions */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                type="button"
                onClick={copy}
                data-testid="share-copy-btn"
                className="py-3 border border-white/15 text-white/85 font-body text-[10px] tracking-[0.2em] uppercase hover:border-ire-accent hover:text-ire-accent transition-all flex items-center justify-center gap-2"
              >
                {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy URL</>}
              </button>
              <a
                href={lastUrl}
                target="_blank"
                rel="noreferrer"
                className="py-3 border border-white/15 text-white/85 font-body text-[10px] tracking-[0.2em] uppercase hover:border-ire-accent hover:text-ire-accent transition-all flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Preview
              </a>
            </div>

            {/* Quick-share to common channels */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(message)}`}
                target="_blank"
                rel="noreferrer"
                data-testid="share-whatsapp"
                className="py-3 text-center font-body text-[10px] tracking-[0.2em] uppercase text-emerald-300 border border-emerald-400/30 hover:bg-emerald-400/10 transition-all"
              >
                WhatsApp
              </a>
              <a
                href={`mailto:?subject=${encodeURIComponent(`${projectLabel} · Investment case`)}&body=${encodeURIComponent(message)}`}
                data-testid="share-email"
                className="py-3 text-center font-body text-[10px] tracking-[0.2em] uppercase text-sky-300 border border-sky-400/30 hover:bg-sky-400/10 transition-all"
              >
                Email
              </a>
              <a
                href={`sms:?&body=${encodeURIComponent(message)}`}
                data-testid="share-sms"
                className="py-3 text-center font-body text-[10px] tracking-[0.2em] uppercase text-violet-300 border border-violet-400/30 hover:bg-violet-400/10 transition-all"
              >
                SMS
              </a>
            </div>

            {/* URL inspector */}
            <details className="mb-3 group">
              <summary className="flex items-center justify-between cursor-pointer font-body text-[10px] tracking-[0.25em] uppercase text-white/40 hover:text-white/70 transition-colors py-2 list-none">
                <span>Inspect URL</span>
                <ChevronDown className="w-3 h-3 transition-transform group-open:rotate-180" />
              </summary>
              <input
                readOnly
                value={lastUrl}
                onFocus={(e) => e.target.select()}
                className="w-full mt-2 bg-black/60 border border-ire-accent/25 text-ire-accent font-mono text-[11px] py-2.5 px-3 outline-none"
              />
            </details>

            <button
              type="button"
              onClick={reset}
              data-testid="share-reset-btn"
              className="w-full py-2.5 text-white/45 font-body text-[10px] tracking-[0.25em] uppercase hover:text-ire-accent transition-colors"
            >
              ← Generate another link
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 sm:px-8 py-4 border-t border-white/[0.05] flex items-baseline justify-between font-body text-[10px] tracking-[0.2em] uppercase text-white/35">
          <a href="/admin/share" className="hover:text-ire-accent">View all links →</a>
          <span>IRE Team</span>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DealroomShareWidget;
